import React, { useCallback, useEffect, useState } from 'react';
import { Star, Search, Smartphone, ShieldCheck, Eye, BadgeCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
const avatars = ['https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/men/65.jpg', 'https://randomuser.me/api/portraits/men/76.jpg', 'https://randomuser.me/api/portraits/men/52.jpg', 'https://randomuser.me/api/portraits/men/41.jpg', 'https://randomuser.me/api/portraits/men/85.jpg', 'https://randomuser.me/api/portraits/men/64.jpg'];
const statIcons = [Search, Eye, Smartphone, ShieldCheck];
export function TestimonialsSection() {
  const {
    t
  } = useLang();
  const testimonials = t.testimonials.items;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + testimonials.length) % testimonials.length);
  }, [index, testimonials.length]);
  const prev = () => {
    setDirection(-1);
    setIndex(i => (i - 1 + testimonials.length) % testimonials.length);
  };
  const next = () => {
    setDirection(1);
    setIndex(i => (i + 1) % testimonials.length);
  };
  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setIndex(i => (i + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);
  const test = testimonials[index];
  return <section id="resenas" className="py-24 bg-dark-100" data-id="element-503" data-mp="2m1xry6ua7c4t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-504" data-mp="2h1t7k5q95x9b">
        <div className="text-center mb-12" data-id="element-505" data-mp="2h0i281h5shg3">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-506" data-mp="1af5ocb55232">
            {t.testimonials.title} <br className="hidden md:block" data-id="element-507" data-mp="3sh8bxuksjreg" />
            <span className="text-gold" data-id="element-508" data-mp="3sgggcjr71to6">{t.testimonials.titleHighlight}</span>
          </h2>
          <div className="inline-flex items-center gap-3 bg-dark px-5 py-2.5 rounded-full border border-white/10" data-id="element-509" data-mp="3dz8ikz01pzg">
            <div className="flex gap-0.5" data-id="element-510" data-mp="8c0rz70c7rbg">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" data-id="element-511" data-mp="ddhpcsjm4vna" data-mpi={i} />)}
            </div>
            <span className="text-white font-bold text-sm" data-id="element-512" data-mp="ib9skippk46s">4.9 / 5</span>
            <span className="text-gray-500 text-sm" data-id="element-513" data-mp="3guq4v5qvbjeu">
              {t.testimonials.reviewCount}
            </span>
          </div>
        </div>

        {/* Carousel */}
        <div className="max-w-2xl mx-auto mb-24" data-id="element-514" data-mp="3qs4lb5csk875">
          <div className="relative flex items-center" data-id="element-515" data-mp="3qq9whfyefvxs">
            {/* Prev button */}
            <button onClick={prev} aria-label={t.testimonials.prevLabel} className="hidden sm:flex absolute -left-4 lg:-left-16 z-20 w-12 h-12 items-center justify-center rounded-full bg-dark border border-white/10 text-gray-400 hover:text-gold hover:border-gold/40 transition-colors" data-id="element-516" data-mp="3vjcvi3ly3xld">
              <ChevronLeft className="w-6 h-6" data-id="element-517" data-mp="1g5kjpp7uz1sq" />
            </button>

            {/* Card */}
            <div className="relative w-full overflow-hidden min-h-[340px]" data-id="element-518" data-mp="1g3pzkd4evdpp">
              <AnimatePresence mode="wait" custom={direction} data-id="element-519" data-mp="1b4ms6m0ykvek">
                <motion.div key={index} custom={direction} initial={{
                opacity: 0,
                x: direction * 60
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -60
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="bg-dark p-8 rounded-2xl border border-white/10 relative" data-id="element-520" data-mp="16865zxdbkr44">
                  {/* Header: avatar + name + verified */}
                  <div className="flex items-center gap-4 mb-5" data-id="element-521" data-mp="2dlql43pslkjr">
                    <img src={avatars[index]} alt={test.author} className="w-14 h-14 rounded-full object-cover border-2 border-gold/30" data-id="element-522" data-mp="2ikbebj6mn3yp" />
                    <div className="flex-1 min-w-0" data-id="element-523" data-mp="28snr5uh3s931">
                      <div className="flex items-center gap-1.5" data-id="element-524" data-mp="1uuqteuqwxwms">
                        <p className="text-white font-bold truncate" data-id="element-525" data-mp="1uwk0gb5fuivb">
                          {test.author}
                        </p>
                        <BadgeCheck className="w-4 h-4 text-gold shrink-0" data-id="element-526" data-mp="1kwt19w5xnfow" />
                      </div>
                      <p className="text-gray-500 text-sm truncate" data-id="element-527" data-mp="1pukuqum879v7">
                        {test.role} · {test.location}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4" data-id="element-528" data-mp="3k9ngch85m7su">
                    <div className="flex gap-0.5" data-id="element-529" data-mp="18m1ufoum3yjm">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" data-id="element-530" data-mp="13n8r2p91hnx6" data-mpi={i} />)}
                    </div>
                    <span className="text-gray-600 text-xs" data-id="element-531" data-mp="1icwq1jtr0ahq">
                      {test.timeAgo}
                    </span>
                  </div>

                  <p className="text-gray-300 leading-relaxed text-lg" data-id="element-532" data-mp="1djbojhsy4yds">
                    {test.quote}
                  </p>

                  <p className="mt-5 pt-4 border-t border-white/5 text-xs text-gray-600 flex items-center gap-1.5" data-id="element-533" data-mp="1nbhigx2rdpvm">
                    <BadgeCheck className="w-3.5 h-3.5 text-gold/60" data-id="element-534" data-mp="219o6llhwobi3" />
                    {t.testimonials.verified}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Next button */}
            <button onClick={next} aria-label={t.testimonials.nextLabel} className="hidden sm:flex absolute -right-4 lg:-right-16 z-20 w-12 h-12 items-center justify-center rounded-full bg-dark border border-white/10 text-gray-400 hover:text-gold hover:border-gold/40 transition-colors" data-id="element-535" data-mp="6yj80vloep9m">
              <ChevronRight className="w-6 h-6" data-id="element-536" data-mp="2b09nhco1foxq" />
            </button>
          </div>

          {/* Mobile arrows + dots */}
          <div className="flex items-center justify-center gap-6 mt-8" data-id="element-537" data-mp="2g175np91siz8">
            <button onClick={prev} aria-label={t.testimonials.prevLabel} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full bg-dark border border-white/10 text-gray-400 hover:text-gold transition-colors" data-id="element-538" data-mp="2kzidxvjp2lfc">
              <ChevronLeft className="w-5 h-5" data-id="element-539" data-mp="2yqn84i4amlia" />
            </button>

            <div className="flex gap-2" data-id="element-540" data-mp="2yry9kba9nh68">
              {testimonials.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`${t.testimonials.dotLabelPrefix} ${i + 1}`} className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-gold' : 'w-2.5 bg-white/20 hover:bg-white/40'}`} data-id="element-541" data-mp="33n3w9cbhszvd" data-mpi={i} />)}
            </div>

            <button onClick={next} aria-label={t.testimonials.nextLabel} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full bg-dark border border-white/10 text-gray-400 hover:text-gold transition-colors" data-id="element-542" data-mp="38v3mf724jujz">
              <ChevronRight className="w-5 h-5" data-id="element-543" data-mp="dqrl75p6i3s" />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-544" data-mp="3rrllk9jaywq9">
          {t.testimonials.stats.map((stat, i) => {
          const Icon = statIcons[i];
          return <div key={i} className="bg-dark/50 p-6 rounded-2xl border border-white/5 text-center" data-id="element-545" data-mp="o438h7ix7p6i" data-mpi={i}>
                <div className="flex justify-center mb-4" data-id="element-546" data-mp="jb0c6rmre1mm">
                  <Icon className="w-8 h-8 text-gold" data-id="element-547" data-mp="1303tgdsxfned" />
                </div>
                <div className="text-4xl font-black text-white mb-2" data-id="element-548" data-mp="17sdjruxyeph6">
                  {stat.data}
                </div>
                <p className="text-gray-400 text-sm mb-4 h-10" data-id="element-549" data-mp="t4aaocom6xo5">{stat.desc}</p>
                <div className="bg-white/5 py-2 px-3 rounded-lg text-xs font-medium text-gold" data-id="element-550" data-mp="y1ira8u46tna">
                  {stat.meaning}
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}